import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { QueryColumnSortOrder, SubQueryKind } from '@barthauer/abc/core/data';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxIcon,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import {
  AbcCoreComponent,
  AbcCoreContainerService,
  AbcDropDownBoxComponent,
  AbcTextBoxComponent,
  _AbcButtonComponent,
  _AbcGridColumn,
  _AbcGridComponent,
  _AbcTreeNode,
} from '@barthauer/abc/ui-components';
import { TextEditorButton } from 'devextreme/common';
import { EditorPreparingEvent, FocusedCellChangedEvent } from 'devextreme/ui/data_grid';
import { ValueChangedEvent as DropDownBoxValueChangedEvent } from 'devextreme/ui/drop_down_box';
import { ValueChangedEvent as TextBoxValueChangedEvent } from 'devextreme/ui/text_box';
import { DataFormService } from 'libs/abc/arena/src/lib/core/data-form/data-form.service';
import { ExpressionEditorResult, ValidationType } from 'libs/abc/ui-components/src/lib/core/src/abc-grid/abc-grid';
import { Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { AbcQueriesEditorService } from '../../../abc-queries-editor.service';
import { QueriesTreeService } from '../../../services/queries-tree.service';
import {
  GridDataSource,
  Property,
  QueryProperties,
  QueryPropertiesTranslation,
} from '../../../viewModels/abc-queries-editor.viewModel';
@Component({
  selector: 'abc-opath-queries-editor-query-properties',
  templateUrl: './query-properties.component.html',
  styleUrls: ['./query-properties.component.scss'],
})
export class QueryPropertiesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(_AbcGridComponent) grid: _AbcGridComponent;
  @ViewChild('objectTypeInfoDropDown', { static: true }) objectTypeInfoDropDown: AbcDropDownBoxComponent;
  @ViewChild('subQueryKindDropDownBox') subQueryKindDropDownBox: AbcDropDownBoxComponent;
  @ViewChild('expressionTextBox') expressionTextBox: AbcTextBoxComponent;

  @Input() public set selectedNode(node: _AbcTreeNode<Property>) {
    this.refreshSelectedNode(node);
  }
  @Input() refreshTree: () => void;
  @Input() deleteQueryContent: () => Promise<void>;
  @Input() refreshSelectColumns: (node: _AbcTreeNode<Property>) => void;

  public columns: _AbcGridColumn[] = [];
  public gridDataSource: GridDataSource[] = [];
  public resAsync: Promise<ResourceSet>;
  public query: _AbcTreeNode<Property>;
  public queryBackup: _AbcTreeNode<Property>;
  public selectedRowIndex: number;
  public sortingDropDownDataSource: { caption: string; value: QueryColumnSortOrder }[] = [
    { caption: 'None', value: QueryColumnSortOrder.None },
    { caption: 'Ascending', value: QueryColumnSortOrder.Ascending },
    { caption: 'Descending', value: QueryColumnSortOrder.Descending },
  ];
  public subQueryKindDataSource: { caption: string; value: SubQueryKind }[] = [
    { caption: 'As sub-hierarchy', value: SubQueryKind.Sub },
    { caption: 'Optional join (LEFT JOIN)', value: SubQueryKind.LeftJoin },
    { caption: 'Required join (INNER JOIN)', value: SubQueryKind.InnerJoin },
  ];
  public objectTypeInfosDataSource: { Name: string }[] = [];
  public translation: QueryPropertiesTranslation;

  protected expressionTextBoxButtons: (string | TextEditorButton)[] = [
    {
      location: 'after',
      name: 'open-expression',
      options: { icon: 'more', stylingMode: 'text', onClick: () => this.openExpressionEditor() },
    },
  ];

  private $unsubscribe: Subject<void> = new Subject();
  constructor(
    public abcQueriesEditorService: AbcQueriesEditorService,
    private abcCoreContainerService: AbcCoreContainerService,
    private resourcesService: ResourceManagerService,
    private queriesTreeService: QueriesTreeService,
    private messageBoxService: MessageBoxService
  ) {
    this.resAsync = this.resourcesService.getResourceSet('abc-opath-queries-editor');
  }

  async ngOnInit(): Promise<void> {
    const res = await this.resAsync;

    this.translation = {
      title: res.get('queryProperties-title'),
      objectType: res.get('queryProperties-objectType'),
      expression: res.get('queryProperties-expression'),
      alias: res.get('queryProperties-alias'),
      noDuplicates: res.get('queryProperties-noDuplicates'),
      skipNumberOfRows: res.get('queryProperties-skipNumberOfRows'),
      limitNumberOfRows: res.get('queryProperties-limitNumberOfRows'),
      gridTitle: res.get('queryProperties-gridTitle'),
      gridColumnExpression: res.get('queryProperties-gridColumn-expression'),
      gridColumnAlias: res.get('queryProperties-gridColumn-alias'),
      gridColumnVisible: res.get('queryProperties-gridColumn-visible'),
      gridColumnSorting: res.get('queryProperties-gridColumn-sorting'),
      subQueryKind: res.get('queryProperties-subQueryKind'),
      confirmObjectTypeChange: res.get('queryProperties-confirmObjectTypeChange'),
    };

    this.initializeGrid();

    this.queriesTreeService.expressionEditorValueChanged
      .pipe(skip(1), takeUntil(this.$unsubscribe))
      .subscribe((expressionResult: ExpressionEditorResult) => {
        if (!this.query.data.queryProperties) {
          return;
        }

        if (this.query.parentNode.parentId && this.query.children.length > 0) {
          this.showConfirmationBox(
            () => this.updateQuery(expressionResult, true),
            () => this.updateQuery(expressionResult, false)
          );
        } else {
          this.updateQuery(expressionResult, false);
        }
      });
  }

  public updateQuery(expressionResult: ExpressionEditorResult, deleteFields: boolean): void {
    this.query.data.expression = expressionResult.oPath;
    if (deleteFields) {
      this.query.children = [];
    }
    if (this.query.parentNode.parentId) {
      this.query.data.name = expressionResult.validate.RefObjectTypeName;
      this.query.caption =
        expressionResult.columnTitle !== this.query.caption ? expressionResult.columnTitle : expressionResult.oPath;
      this.query.data.alias = this.query.caption;
      this.refreshSelectColumns(this.query);
    }
    this.refreshTree();
  }

  public handleExpressionChanged(event: TextBoxValueChangedEvent) {
    if (this.query.parentNode.parentId) {
      if (event.previousValue && event.value !== event.previousValue) {
      }
    }
  }
  async ngAfterViewInit(): Promise<void> {
    this.subQueryKindDropDownBox.setupForArrayData(this.subQueryKindDataSource, 'value', 'caption', [
      { dataField: 'caption' },
    ]);
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }

  private initializeGrid() {
    this.grid.editing = { allowUpdating: true, mode: 'cell' };
    this.grid.paging = { pageSize: 5 };
    this.grid.pager = {
      showPageSizeSelector: true,
      allowedPageSizes: [2, 5, 10, 20],
      showInfo: true,
      displayMode: 'full',
      showNavigationButtons: true,
      visible: this.gridDataSource.length > 2,
    };

    this.createGridColumns();
  }

  private createGridColumns() {
    const columnExpression: _AbcGridColumn = new _AbcGridColumn();
    columnExpression.dataField = 'expression';
    columnExpression.caption = this.translation?.gridColumnExpression;
    columnExpression.visibleIndex = 0;
    columnExpression.width = '40%';
    columnExpression.editCellTemplate = async (container) => {
      const tamplateContainer = document.createElement('div');
      tamplateContainer.className = 'expression-template';
      const butonComponentRef: AbcCoreComponent = this.abcCoreContainerService.createComponent(
        null,
        _AbcButtonComponent
      );
      const textBoxRef: AbcCoreComponent = this.abcCoreContainerService.createComponent(null, AbcTextBoxComponent);
      let textBox: AbcTextBoxComponent = textBoxRef.componentRef.instance as AbcTextBoxComponent;
      textBox.value = this.gridDataSource[this.selectedRowIndex].expression;
      textBox.disabled = true;
      let button: _AbcButtonComponent;
      button = butonComponentRef.componentRef.instance as _AbcButtonComponent;
      button.icon = 'more';
      button.width = '30px';
      button.height = '33px';
      button.onClick.subscribe(async () => {
        await this.queriesTreeService.openOpathExpression(
          ValidationType.Column,
          this.query.children[this.selectedRowIndex],
          this.abcQueriesEditorService.queryDocument?.ParameterDefinitions || [],
          null,
          this.translation.expression
        );
        this.refreshTree();
      });
      tamplateContainer.appendChild(textBoxRef.domElement);
      tamplateContainer.appendChild(butonComponentRef.domElement);
      container.append(tamplateContainer);
    };

    const columnAlias: _AbcGridColumn = new _AbcGridColumn();
    columnAlias.dataField = 'alias';
    columnAlias.caption = this.translation?.gridColumnAlias;
    columnAlias.visibleIndex = 1;
    columnAlias.width = '40%';
    columnAlias.editCellTemplate = async (container) => {
      const tamplateContainer = document.createElement('div');
      tamplateContainer.className = 'expression-template';
      const butonComponentRef: AbcCoreComponent = this.abcCoreContainerService.createComponent(
        null,
        _AbcButtonComponent
      );
      const textBoxRef: AbcCoreComponent = this.abcCoreContainerService.createComponent(null, AbcTextBoxComponent);
      let textBox: AbcTextBoxComponent = textBoxRef.componentRef.instance as AbcTextBoxComponent;
      textBox.value = this.gridDataSource[this.selectedRowIndex].alias;
      let button: _AbcButtonComponent;
      button = butonComponentRef.componentRef.instance as _AbcButtonComponent;
      button.icon = 'save';
      button.width = '30px';
      button.height = '33px';
      button.onClick.subscribe(() => {
        if (textBox.value.length > 0) {
          this.query.children[this.selectedRowIndex].caption = textBox.value;
          this.refreshSelectedNode(this.query);
          this.refreshTree();
        }
      });
      tamplateContainer.appendChild(textBoxRef.domElement);
      tamplateContainer.appendChild(butonComponentRef.domElement);
      container.append(tamplateContainer);
    };

    const columnSorting: _AbcGridColumn = new _AbcGridColumn();
    columnSorting.dataField = 'sorting';
    columnSorting.caption = this.translation?.gridColumnSorting;
    columnSorting.visibleIndex = 2;
    columnSorting.width = '10%';
    columnSorting.allowEditing = false;
    columnSorting.lookup = {
      dataSource: this.sortingDropDownDataSource,
      valueExpr: 'value',
      displayExpr: 'caption',
    };
    columnSorting.setCustomCellEditor(
      this.abcCoreContainerService,
      async (dropdown: AbcDropDownBoxComponent, options) => {
        dropdown.acceptCustomValue = false;
        dropdown.setupForArrayData(this.sortingDropDownDataSource, 'value', 'caption', [{ dataField: 'caption' }]);
        dropdown.value = this.query.children[this.selectedRowIndex].data.columnProperties.sorting;
        dropdown.onValueChanged.subscribe((event: DropDownBoxValueChangedEvent) => this.changeSorting(event));
      }
    );

    const columnVisible: _AbcGridColumn = new _AbcGridColumn();
    columnVisible.dataField = 'visible';
    columnVisible.caption = this.translation?.gridColumnVisible;
    columnVisible.visibleIndex = 3;

    this.columns.push(columnExpression);
    this.columns.push(columnVisible);
    this.columns.push(columnAlias);
    this.columns.push(columnSorting);
  }

  private createObjectTypeInfosDataSource(): Promise<void> {
    return new Promise((resolve) => {
      this.objectTypeInfosDataSource = [];

      for (let objectType of this.abcQueriesEditorService.objectTypeInfos) {
        this.objectTypeInfosDataSource.push({ Name: objectType.Name });
      }
      resolve();
    });
  }
  public lookupNotEditable(event: EditorPreparingEvent) {
    if (event['caption'] === 'Sorting' || event['caption'] === 'Visible') {
      event.editorOptions.searchEnabled = false;
    }
  }

  private capitalize(text: string): string {
    return text[0].toUpperCase() + text.slice(1);
  }

  private async showConfirmationBox(yesAction: () => void, noAction: () => void): Promise<void> {
    const settings = new MessageSettings();
    settings.mode = MessageMode.Warning;
    settings.icon = MessageBoxIcon.Question;
    settings.button = MessageBoxButtons.YesIgnoreCancel;
    settings.message = this.translation.confirmObjectTypeChange;
    this.messageBoxService
      .showMessageBox(settings)
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe(async (result: DialogResult) => {
        switch (result['result']) {
          case DialogResult.Yes:
            await yesAction();
            break;
          case DialogResult.Ignore:
            return noAction();
          case DialogResult.Cancel:
            this.objectTypeInfoDropDown.value = this.query.data.name;
            break;
          default:
            break;
        }
      });
  }

  public refresh(): void {
    if (this.notEqual(this.query, this.queryBackup)) {
      this.refreshTree();
      this.queryBackup = this.cloneNode(this.query);
    }
  }

  private cloneNode(node: _AbcTreeNode<Property>): _AbcTreeNode<Property> {
    const backup: _AbcTreeNode<Property> = new _AbcTreeNode<Property>();
    backup.caption = node.caption;

    backup.data = new Property();
    backup.data.alias = node.data.alias;
    backup.data.expression = node.data.expression;

    backup.data.queryProperties = new QueryProperties({
      isDuplicated: node?.data?.queryProperties?.isDuplicated,
      limitRowsNbr: node?.data?.queryProperties?.limitRowsNbr,
      skippedRowsNbr: node?.data?.queryProperties?.skippedRowsNbr,
      subQueryKind: node?.data?.queryProperties?.subQueryKind,
    });

    return backup;
  }

  private notEqual(node1: _AbcTreeNode<Property>, node2: _AbcTreeNode<Property>): boolean {
    return (
      node1.caption !== node2.caption ||
      node1.data.alias !== node2.data.alias ||
      node1.data.expression !== node2.data.expression ||
      node1.data.queryProperties.isDuplicated !== node2.data.queryProperties.isDuplicated ||
      node1.data.queryProperties.limitRowsNbr !== node2.data.queryProperties.limitRowsNbr ||
      node1.data.queryProperties.skippedRowsNbr !== node2.data.queryProperties.skippedRowsNbr ||
      node1.data.queryProperties.subQueryKind !== node2.data.queryProperties.subQueryKind
    );
  }

  public async changeQueryObjectType(e: DropDownBoxValueChangedEvent): Promise<void> {
    const updateQuery = () => {
      this.query.caption = e.value;
      this.query.data.name = e.value;
      this.query.data.alias = '';
      this.query.data.expression = '';
      this.refreshSelectColumns(this.query);
      this.refreshTree();
    };

    const deleteQueryContent = async (): Promise<void> => {
      await this.deleteQueryContent();
      updateQuery();
    };

    if (e.value !== this.query.data.name) {
      if (this.query.children.length) {
        await this.showConfirmationBox(deleteQueryContent, updateQuery);
      } else {
        updateQuery();
      }
    }
  }

  public async refreshSelectedNode(node: _AbcTreeNode<Property>): Promise<void> {
    this.query = node;
    this.queryBackup = this.cloneNode(node);
    if (!this.query.parentNode.parentId && this.objectTypeInfoDropDown) {
      await this.createObjectTypeInfosDataSource();

      this.objectTypeInfoDropDown.setupForArrayData(
        this.objectTypeInfosDataSource,
        'Name',
        'Name',
        [{ dataField: 'Name' }],
        this.objectTypeInfosDataSource.find((row) => row.Name === this.query.data.name)?.Name
      );
    }

    this.gridDataSource = [];

    node.children.map((child: _AbcTreeNode<Property>) => {
      if (child.data.columnProperties) {
        this.gridDataSource.push(
          new GridDataSource({
            id: child.id,
            alias: child.caption,
            expression: child.data.expression,
            ...child.data.columnProperties,
          })
        );
      }
    });
  }

  public async openExpressionEditor(): Promise<void> {
    await this.queriesTreeService.openOpathExpression(
      ValidationType.Query,
      this.query,
      this.abcQueriesEditorService.queryDocument?.ParameterDefinitions || [],
      null,
      this.translation.expression
    );
  }
  public updateRow(event: any): void {
    this.query.children[this.selectedRowIndex].data.columnProperties.visible = event.data.visible;
    this.refreshSelectedNode(this.query);
    this.refreshTree();
  }

  public changeAlias(event: TextBoxValueChangedEvent): void {
    event.event &&
      ((this.query.caption = event.value?.trim() !== '' ? event.value : this.query.data.name), this.refreshTree());
  }

  public changeSorting(event: DropDownBoxValueChangedEvent): void {
    this.query.children[this.selectedRowIndex].data.columnProperties.sorting = event.value;
    this.refreshSelectedNode(this.query);
    this.refreshTree();
  }

  public handleRowIndex(event: FocusedCellChangedEvent): void {
    this.selectedRowIndex = event.rowIndex;
  }
}
